import React from 'react';
import { format } from 'date-fns';

// Shadcn Components
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";

// Icons
import { AlertCircle, Receipt } from "lucide-react";

// Types
import { Transaction } from '@/types/organization/finances';

// Utility function to format currency
const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(amount / 100);
};

// Helper function to check if transaction is fully refunded
const isFullyRefunded = (transaction: Transaction) => {
    // Check if the transaction is marked as refunded
    // This distinguishes between actual refunds and bursary approvals
    return transaction.refunded === true;
};

interface TransactionTableProps {
    transactions: Transaction[];
    isLoading: boolean;
    error: string | null;
}

const TicketSalesTable: React.FC<TransactionTableProps> = ({ transactions, isLoading, error }) => {
    if (isLoading) {
        return (
            <div className="hidden md:block w-full">
                <div className="border rounded-lg overflow-hidden w-full">
                    <div className="min-h-[500px] max-h-[500px] overflow-auto w-full bg-gray-50">
                        <div className="grid w-full bg-white" style={{ 
                            gridTemplateColumns: 'minmax(140px, 1.5fr) minmax(140px, 2fr) minmax(100px, 1.2fr) minmax(80px, 1fr) minmax(80px, 1fr)'
                        }}>
                            {/* Header */}
                            <div className="sticky top-0 bg-black z-10 border-b py-2 px-4 font-medium text-white">Customer</div>
                            <div className="sticky top-0 bg-black z-10 border-b py-2 px-4 font-medium text-white text-center">Event</div>
                            <div className="sticky top-0 bg-black z-10 border-b py-2 px-4 font-medium text-white text-center">Amount</div>
                            <div className="sticky top-0 bg-black z-10 border-b py-2 px-4 font-medium text-white text-center">Date</div>
                            <div className="sticky top-0 bg-black z-10 border-b py-2 px-4 font-medium text-white text-center">ID</div>

                            {/* Loading Skeletons */}
                            {[...Array(12)].map((_, index) => (
                                <React.Fragment key={index}>
                                    <div className="border-b py-3 px-4">
                                        <div className="min-w-0 space-y-1">
                                            <div className="h-4 bg-muted rounded animate-pulse" />
                                            <div className="h-3 bg-muted rounded animate-pulse w-3/4" />
                                        </div>
                                    </div>
                                    <div className="border-b py-3 px-4 flex items-center">
                                        <div className="h-4 bg-muted rounded animate-pulse w-full" />
                                    </div>
                                    <div className="border-b py-3 px-4 font-medium text-green-600 whitespace-nowrap text-sm flex items-center justify-center">
                                        <div className="h-4 bg-muted rounded animate-pulse w-20" />
                                    </div>
                                    <div className="border-b py-3 px-4 whitespace-nowrap text-sm flex items-center justify-center">
                                        <div className="h-4 bg-muted rounded animate-pulse w-24" />
                                    </div>
                                    <div className="border-b py-3 px-4 flex items-center">
                                        <div className="h-3 bg-muted rounded animate-pulse w-full" />
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
            </Alert>
        );
    }

    if (transactions.length === 0) {
        return (
            <div className="text-center py-12">
                <Receipt className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-4 text-lg font-semibold">No transactions found</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                    There are no transactions to display at this time.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Mobile View */}
            <div className="md:hidden space-y-4">
                {transactions.map((transaction) => (
                    <Card key={transaction.uuid}>
                        <CardContent className="p-6">
                            <div className="space-y-4">
                                <div className="flex justify-between items-start gap-4">
                                    <div className="min-w-0 flex-1">
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <p className="font-medium truncate text-base">
                                                        {transaction.ticket?.customer_name || transaction.billing_details.name || 'N/A'}
                                                    </p>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    {transaction.ticket?.customer_name || transaction.billing_details.name || 'N/A'}
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <p className="text-sm text-muted-foreground truncate mt-1">
                                                        {transaction.ticket?.customer_email || transaction.billing_details.email || 'N/A'}
                                                    </p>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    {transaction.ticket?.customer_email || transaction.billing_details.email || 'N/A'}
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                    <div className="text-right">
                                        <p className={`font-medium text-base whitespace-nowrap ${isFullyRefunded(transaction) ? 'text-red-500 line-through' : 'text-green-600'}`}>
                                            {formatCurrency(transaction.amount)}
                                        </p>
                                        {isFullyRefunded(transaction) && (
                                            <p className="text-xs text-red-500">Refunded</p>
                                        )}
                                    </div>
                                </div>
                                <div className="space-y-2 text-sm text-muted-foreground">
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <p className="truncate">{transaction.event?.title || 'N/A'}</p>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                {transaction.event?.title || 'N/A'}
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                    <p>{format(new Date(transaction.createdAt), 'MMM d, yyyy')}</p>
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <p className="text-xs truncate">ID: {transaction.uuid}</p>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                {transaction.uuid}
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Desktop View */}
            <div className="hidden md:block w-full">
                <div className="border rounded-lg overflow-hidden w-full">
                    <div className="min-h-[500px] max-h-[500px] overflow-auto w-full bg-gray-50">
                        <div className="grid w-full bg-white" style={{ 
                            gridTemplateColumns: 'minmax(140px, 1.5fr) minmax(140px, 2fr) minmax(100px, 1.2fr) minmax(80px, 1fr) minmax(80px, 1fr)'
                        }}>
                            {/* Header */}
                            <div className="sticky top-0 bg-black z-10 border-b py-2 px-4 font-medium text-white">Customer</div>
                            <div className="sticky top-0 bg-black z-10 border-b py-2 px-4 font-medium text-white text-center">Event</div>
                            <div className="sticky top-0 bg-black z-10 border-b py-2 px-4 font-medium text-white text-center">Amount</div>
                            <div className="sticky top-0 bg-black z-10 border-b py-2 px-4 font-medium text-white text-center">Date</div>
                            <div className="sticky top-0 bg-black z-10 border-b py-2 px-4 font-medium text-white text-center">ID</div>

                            {/* Rows */}
                            {transactions.map((transaction) => (
                                <React.Fragment key={transaction.uuid}>
                                    <div className="border-b py-2 px-4">
                                        <div className="min-w-0 space-y-0.5">
                                            <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <p className="font-medium truncate text-sm">
                                                            {transaction.ticket?.customer_name || transaction.billing_details.name || 'N/A'}
                                                        </p>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        {transaction.ticket?.customer_name || transaction.billing_details.name || 'N/A'}
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                            <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <p className="text-xs text-muted-foreground truncate">
                                                            {transaction.ticket?.customer_email || transaction.billing_details.email || 'N/A'}
                                                        </p>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        {transaction.ticket?.customer_email || transaction.billing_details.email || 'N/A'}
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </div>
                                    </div>
                                    <div className="border-b py-2 px-4 flex items-center">
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <p className="truncate text-sm w-full text-center">{transaction.event?.title || 'N/A'}</p>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    {transaction.event?.title || 'N/A'}
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                    <div className="border-b py-2 px-4 font-medium whitespace-nowrap text-sm flex items-center justify-center">
                                        <div className="text-center">
                                            <p className={isFullyRefunded(transaction) ? 'text-red-500 line-through' : 'text-green-600'}>
                                                {formatCurrency(transaction.amount)}
                                            </p>
                                            {isFullyRefunded(transaction) && (
                                                <p className="text-xs text-red-500">Refunded</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="border-b py-2 px-4 whitespace-nowrap text-sm flex items-center justify-center">
                                        {format(new Date(transaction.createdAt), 'MMM d, yyyy')}
                                    </div>
                                    <div className="border-b py-2 px-4 flex items-center">
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <p className="text-xs text-muted-foreground truncate w-full text-center">{transaction.uuid}</p>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    {transaction.uuid}
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TicketSalesTable; 